import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import swrConfig from '../../utils/swrConfig';
import { OperationVariables, shopifyGraphQL, ShopifyGraphQLError, ShopifyGraphQLOptions } from './shopify-graphql';
import { gatewayShopifyGraphQL, useShopifyGraphQLGateway } from './shopify-graphql-gateway';

/**
 * Per-operation Admin API version, plus any SWR option this hook doesn't set itself.
 *
 * `fetcher` is omitted because the hook always supplies its own, so anything passed here would be
 * silently ignored. The motivating option is `keepPreviousData`, which keeps the previous result on
 * screen while a new key loads instead of blanking to `undefined` — worth enabling whenever the key
 * is derived from data that changes as the user works, since without it the UI collapses to its
 * empty state for the duration of the refetch.
 */
export type UseShopifyGraphQLSWROptions<TData = unknown> = ShopifyGraphQLOptions &
    Omit<SWRConfiguration<TData, ShopifyGraphQLError>, 'fetcher'>;

export function useShopifyGraphQLSWR<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
    key: string | null,
    query: string,
    variables?: TVariables,
    path = 'products',
    { version, ...swrOptions }: UseShopifyGraphQLSWROptions<TData> = {}
) {
    const [cursors, setCursors] = useState<(string | null)[]>([null]);
    const [pageIndex, setPageIndex] = useState(0);
    const _variables = useRef<TVariables | undefined>(variables);

    const cursor = cursors[pageIndex];
    const variablesWithCursor = cursor ? { ..._variables.current, after: cursor } : _variables.current;

    useEffect(() => {
        if (!isEqual(variables, _variables.current)) {
            _variables.current = variables;
            setCursors([null]);
            setPageIndex(0);
        }
    }, [variables]);

    // When a gateway config is provided, route the operation through the backend GraphQL proxy
    // instead of App Bridge. The gateway URL is part of the SWR key so direct-store and
    // proxied-store responses never share a cache entry.
    const gateway = useShopifyGraphQLGateway();

    // The API version is part of the key so an operation overriding it never shares a cache entry
    // with the same operation on the app's default version.
    const { data, error, isLoading, mutate } = useSWR<TData, ShopifyGraphQLError>(
        key ? [key, variablesWithCursor, gateway?.url ?? null, version ?? null] : null,
        () =>
            gateway
                ? gatewayShopifyGraphQL<TData, TVariables>(gateway, query, variablesWithCursor as TVariables, {
                      version,
                  })
                : shopifyGraphQL<TData, TVariables>(query, variablesWithCursor as TVariables, { version }),
        { ...swrConfig, ...swrOptions }
    );

    const pageInfo = data?.[path]?.pageInfo;
    const hasNextPage = pageInfo?.hasNextPage ?? false;
    const hasPrevPage = pageIndex > 0;

    const next = useCallback(() => {
        if (!hasNextPage || !pageInfo?.endCursor || isLoading) {
            return;
        }
        const nextIndex = pageIndex + 1;
        setCursors((prev) => {
            const updated = [...prev];
            updated[nextIndex] = pageInfo.endCursor!;
            return updated;
        });
        setPageIndex(nextIndex);
    }, [hasNextPage, pageInfo?.endCursor, isLoading, pageIndex]);

    const prev = useCallback(() => {
        if (!hasPrevPage || isLoading) {
            return;
        }
        setPageIndex((i) => i - 1);
    }, [hasPrevPage, isLoading]);

    const reset = useCallback(() => {
        setCursors([null]);
        setPageIndex(0);
        mutate();
    }, [mutate]);

    return {
        data,
        error,
        isLoading,
        hasNextPage: hasNextPage && !isLoading,
        hasPrevPage: hasPrevPage && !isLoading,
        mutate,
        refetch: () => mutate(),
        next,
        prev,
        reset,
    };
}
